import { parseExif } from './exif';
import type { PhotoAsset } from '../types';

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
const SUPPORTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

function extensionOf(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}

export function isSupportedImage(file: File): boolean {
  return SUPPORTED_MIME.includes(file.type) || SUPPORTED_EXTENSIONS.includes(extensionOf(file.name));
}

function loadImageFromBlob(blob: Blob): Promise<{ image: HTMLImageElement; objectUrl: string }> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => resolve({ image, objectUrl });
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('error.decode'));
    };
    image.src = objectUrl;
  });
}

async function normalizeHeic(file: File): Promise<Blob> {
  const extension = extensionOf(file.name);
  const needsConversion = file.type.includes('heic') || file.type.includes('heif') || ['heic', 'heif'].includes(extension);
  if (!needsConversion) return file;

  const module = await import('heic2any');
  const result = await module.default({ blob: file, toType: 'image/png', quality: 0.96 });
  return Array.isArray(result) ? result[0] : result;
}

export async function loadPhotoAsset(file: File): Promise<PhotoAsset> {
  if (!isSupportedImage(file)) {
    throw new Error('error.unsupported');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error('error.tooLarge');
  }

  const [exif, displayBlob] = await Promise.all([parseExif(file), normalizeHeic(file)]);
  const { image, objectUrl } = await loadImageFromBlob(displayBlob);

  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
    fileName: file.name,
    fileType: file.type || `image/${extensionOf(file.name)}`,
    size: file.size,
    width: image.naturalWidth,
    height: image.naturalHeight,
    objectUrl,
    image,
    exif,
    originalFile: file
  };
}

export function revokePhotoAsset(asset: PhotoAsset | null): void {
  if (asset) URL.revokeObjectURL(asset.objectUrl);
}
